import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const STRINGS = {
    es: {
        app_name: "soyapostol",
        tagline: "",
        nav: {
            dashboard: "Inicio",
            readings: "Lecturas",
            liturgy: "Liturgia de las Horas",
            prayers: "Oraciones",
            rosary: "Santo Rosario",
            examen: "Examen de Conciencia",
            news: "Noticias",
            bible: "Biblia",
            catechism: "Catecismo",
            favorites: "Favoritos",
            login: "Entrar",
            register: "Crear cuenta",
            logout: "Salir",
            more: "Más",
        },
        common: {
            loading: "Cargando…",
            error: "Error",
            today: "Hoy",
            source: "Fuente",
            save_favorite: "Guardar en favoritos",
            saved: "Guardado",
            removed: "Eliminado",
            remove: "Quitar",
            email: "Correo electrónico",
            password: "Contraseña",
            name: "Nombre",
            sign_in: "Iniciar sesión",
            create_account: "Crear cuenta",
            no_account: "¿No tienes cuenta?",
            have_account: "¿Ya tienes cuenta?",
            welcome_back: "Bienvenido de nuevo",
            join_apostol: "Únete a Apostol",
            select: "Seleccionar",
            view: "Ver",
            search: "Buscar",
            refresh: "Actualizar",
            back_to_menu: "Volver al menú",
            back_to_top: "Volver arriba",
            yes: "Sí",
            cancel: "Cancelar",
            show_more: "Leer más",
            show_less: "Cerrar",
        },
        favorites: {
            search_placeholder: "Buscar entre mis favoritos…",
            no_results: 'Sin resultados para "{q}".',
        },
        sections: {
            readings_desc: "Las lecturas del día con comentario del Evangelio",
            liturgy_desc: "Oración litúrgica de las Horas",
            prayers_desc: "Compendio de oraciones católicas",
            rosary_desc: "Reza el Santo Rosario, día por día",
            examen_desc: "Recursos para tu examen de conciencia",
            news_desc: "Las últimas noticias de Vatican News",
            bible_desc: "La Sagrada Biblia católica completa",
            catechism_desc: "Catecismo de la Iglesia Católica",
            favorites_desc: "Tus textos guardados",
        },
        readings: {
            first: "1ra Lectura",
            psalm: "Salmo",
            second: "2da Lectura",
            gospel: "Evangelio",
            gospel_acclamation: "Aclamación al Evangelio",
            commentary: "Comentario",
            universalis_unavailable: "Content temporarily unavailable",
            reflection_eyebrow: "Comentario al Evangelio",
            reflection_title: "Reflexión del día",
            reflection_credit: "Comentario por gentileza de",
            eod_eyebrow: "Otra reflexión",
            eod_title: "Comentario de Evangelio del Día",
            eod_unavailable: "Comentario de Evangelio del Día no disponible por el momento.",
            view_usccb: "Lecturas oficiales (USCCB)",
            prev_day: "Día anterior",
            next_day: "Día siguiente",
            pick_date: "Elegir fecha",
            go_today: "Hoy",
        },
        usccb: {
            title: "Lecturas Oficiales (USCCB)",
            close: "Regresar",
            refresh: "Refrescar",
            loading: "Cargando lecturas oficiales…",
            open_external: "Abrir en USCCB.org",
            blocked_eyebrow: "Visualización restringida",
            blocked_title: "USCCB no permite mostrar su sitio dentro de la app",
            blocked_subtitle: "Por políticas de seguridad del propio sitio (X-Frame-Options), el contenido no puede embeberse. Ábrelo en una pestaña nueva para leerlo.",
            try_again: "Intentar de nuevo",
        },
        news: {
            source_credit: "Fuente:",
            empty: "No hay noticias por el momento.",
            tab_all: "Todas",
            tabs_label: "Fuentes de noticias",
        },
        pwa: {
            eyebrow: "Disponible como app",
            title: "Instala soyapostol en tu dispositivo",
            subtitle_native: "Acceso directo desde tu pantalla de inicio, sin barra del navegador y con soporte offline.",
            subtitle_ios: "Desde tu iPhone o iPad puedes agregar esta app a tu pantalla de inicio en dos pasos.",
            ios_step_1: "Toca el botón Compartir",
            ios_step_2: "Selecciona «Añadir a pantalla de inicio»",
            install_button: "Instalar la app",
            install_button_short: "Instalar",
            dismiss: "Ocultar este aviso",
        },
        rosary: {
            title: "Santo Rosario",
            today_eyebrow: "Misterios de hoy",
            choose_eyebrow: "Misterios seleccionados",
            cover_subtitle: "Toma tu rosario, respira profundo y comienza con el corazón abierto a María.",
            begin: "Comenzar",
            change_mystery: "Cambiar misterio",
            previous: "Anterior",
            next: "Siguiente",
            restart: "Reiniciar",
            help: "Desliza, toca los lados o usa las flechas del teclado para avanzar.",
            mystery_n: "{n}.º Misterio",
            fruit_label: "Fruto:",
            meditation_label: "Meditación:",
            decade_n: "Decena {n}",
            intro_three_eyebrow: "Tres Avemarías",
            offering_eyebrow: "Intenciones",
            after_decade_label: "Tras la decena",
            after_decade_title: "Oraciones de la decena",
            prayer_label: "Oración",
            bead_progress: "{n} de {total}",
            tap_bead: "Tocar para contar",
            tap_anywhere: "Toca cualquier parte de la tarjeta para contar",
            decade_done: "Decena completa",
            decade_done_hint: "Decena completa · pulsa Siguiente",
            reset: "Reiniciar contador",
            completed_eyebrow: "Bien hecho",
            completed_title: "Has completado el Santo Rosario",
            completed_subtitle: "Que la Virgen María te acompañe hoy y siempre.",
            pray_again: "Rezar de nuevo",
        },
        examen: {
            title: "Examen de Conciencia",
            cover_title: "Examen de Conciencia",
            cover_subtitle: "Prepárate para el sacramento de la Reconciliación.",
            privacy_notice: "Privacidad: todas tus respuestas se guardan solo en este dispositivo.",
            choose_profile: "Elige tu examen",
            change_profile: "Cambiar examen",
            commandment: "Mandamiento",
            your_state: "Tu estado",
            selected_count: "Marcadas en este examen",
            marked_count: "{n} marcadas",
            marked_count_one: "{n} marcada",
            cumulative_title: "Tu examen acumulado",
            see_summary: "Ver resumen",
            summary_eyebrow: "Examen acumulado",
            summary_title: "Resumen para la Confesión",
            summary_subtitle: "{n} pensamiento(s), palabra(s) u obra(s) para llevar al confesonario.",
            summary_empty: "No has marcado ninguna pregunta todavía.",
            back_to_questions: "Volver al examen",
            contrition_eyebrow: "Para cerrar",
            contrition_title: "Acto de Contrición",
            start_over: "Empezar de nuevo",
            start_over_confirm: "¿Borrar todas tus marcas y empezar de nuevo?",
            finish_confession: "Borrar y salir",
            finish_confession_confirm: "Se borrará todo de forma permanente. ¿Terminar confesión?",
            finish_done_title: "Paz sea contigo",
            finish_done_subtitle: "Todas tus marcas han sido borradas. Ve en paz.",
            return_home: "Volver al inicio",
            scripture_eyebrow: "Palabra de Dios",
            prayer_eyebrow: "Oración para cerrar",
            profile: {
                adults: "Adultos",
                married_couples: "Casados",
                single_adults: "Solteros",
                teenagers: "Adolescentes",
                beatitudes: "Bienaventuranzas",
                triple_love: "Triple amor",
                capital_sins: "Siete pecados capitales",
                virtues: "Virtudes",
                mercy_works: "Obras de misericordia",
                ignatian_daily: "Examen diario (ignaciano)",
                charity_hymn: "Himno de la Caridad",
                holy_spirit: "Dones del Espíritu Santo",
                youth: "Jóvenes",
                work: "Trabajo profesional",
                family: "Matrimonio y familia",
                digital: "Vida digital",
            },
            profile_desc: {
                adults: "Examen general con los Diez Mandamientos.",
                married_couples: "Incluye preguntas específicas para esposos.",
                single_adults: "Incluye preguntas específicas para solteros.",
                teenagers: "Incluye preguntas específicas para jóvenes.",
                beatitudes: "Examen alternativo basado en las ocho Bienaventuranzas.",
                triple_love: "Basado en el Gran Mandamiento: amor a Dios, al prójimo y a uno mismo.",
                capital_sins: "Análisis de las raíces de nuestras faltas: los siete pecados capitales.",
                virtues: "Las virtudes teologales y cardinales que guían la vida cristiana.",
                mercy_works: "Las obras de misericordia corporales y espirituales.",
                ignatian_daily: "Revisión diaria del corazón en cinco pasos (San Ignacio de Loyola).",
                charity_hymn: "Basado en 1 Corintios 13: las cualidades del amor verdadero.",
                holy_spirit: "Cómo vives los dones del Espíritu Santo recibidos en la Confirmación.",
                youth: "Guía práctica para jóvenes: casa, escuela y amigos.",
                work: "Para profesionales: integridad, excelencia y liderazgo cristiano.",
                family: "Para esposos y padres: vivir la vocación del hogar cristiano.",
                digital: "Uso de redes, internet y pantallas a la luz de la fe.",
            },
        },
        catechism: {
            search_placeholder: "Buscar por número (ej. 144) o por texto…",
            results_count: "{count} resultado(s)",
            total_count: "{count} párrafos",
            no_results: "Sin resultados. Prueba otro término o número.",
            load_more: "Cargar más",
            copy_paragraph: "Copiar párrafo",
            share_paragraph: "Compartir",
            remove_favorite: "Quitar de favoritos",
            paragraph_copied: "Párrafo copiado",
            share_title: "Catecismo de la Iglesia Católica",
            back_to_index: "Índice",
            coming_soon_eyebrow: "Próximamente",
            coming_soon_title: "Catecismo en español",
            coming_soon_body: "Estamos preparando la edición oficial en español del Catecismo de la Iglesia Católica. Mientras tanto, puedes consultarlo en inglés cambiando el idioma desde Ajustes.",
        },
        bible: {
            search_placeholder: "Buscar palabra clave o referencia (ej. \"Juan 3:16\"). Pulsa Enter para saltar.",
            results_count: "{count} resultado(s)",
            no_results: "Sin resultados. Prueba otra palabra o referencia.",
            go_to: "Ir a",
            copy_verse: "Copiar versículo",
            share_verse: "Compartir",
            remove_favorite: "Quitar de favoritos",
            verse_copied: "Versículo copiado",
            share_title: "Versículo de la Biblia",
            saved_hint: "Guardado en favoritos",
            back_to_index: "Índice",
        },
        prayers_actions: {
            copy: "Copiar oración",
            share: "Compartir",
            copied: "Oración copiada",
            share_title: "Oración",
            long_press_hint: "Mantén presionado para más opciones",
        },
        news_actions: {
            copy: "Copiar noticia",
            share: "Compartir noticia",
            copied: "Noticia copiada",
        },
        votd: {
            title: "Versículo del día",
            open_bible: "Ir a la Biblia",
        },
        quickview: {
            open_bible: "Ver en la Biblia",
            not_found: "No se pudo cargar la cita {ref}.",
        },
        admin: {
            eyebrow: "Panel de administrador",
            new_prayer: "Nueva oración",
            edit_prayer: "Editar oración",
            db_hint: "Las oraciones se guardan en la base de datos. Tus cambios persisten para todos los usuarios.",
            title: "Título",
            category: "Categoría",
            choose_category: "Elige una categoría…",
            new_category: "+ Nueva categoría",
            new_category_name: "Nombre de la nueva categoría",
            content: "Texto de la oración",
            content_placeholder: "Pega aquí el texto. Una línea en blanco separa párrafos.",
            add: "Añadir oración",
            save: "Guardar cambios",
            cancel: "Cancelar",
            edit: "Editar",
            delete: "Eliminar",
            stored_count: "{count} oraciones",
            empty: "Aún no hay oraciones.",
            no_filter_matches: "Sin resultados para ese filtro.",
            filter_placeholder: "Filtrar por título o categoría…",
            imported_badge: "Importada",
            created: "Oración añadida",
            updated: "Oración actualizada",
            deleted: "Oración eliminada",
            confirm_delete: "¿Eliminar «{title}»?",
            missing_fields: "Completa título, categoría y texto.",
        },
        auth: {
            forgot_password: "¿Olvidaste tu contraseña?",
            recover_eyebrow: "Recuperar acceso",
            forgot_password_intro: "Te enviaremos un enlace para restablecer tu contraseña. Caduca en 60 minutos.",
            send_reset_link: "Enviar enlace",
            reset_link_issued: "Enlace de recuperación creado",
            reset_link_demo_hint: "Modo demo: copia el enlace que aparece abajo para continuar.",
            reset_link_sent_generic: "Si el correo existe, te enviamos un enlace de recuperación.",
            check_inbox_title: "Revisa tu bandeja de entrada",
            check_inbox_hint: "Si {email} coincide con una cuenta, acabamos de enviarte un correo con un enlace para restablecer tu contraseña.",
            check_spam_hint: "El enlace caduca en 60 minutos. Si no lo ves, revisa la carpeta de spam o promociones.",
            demo_link_label: "Tu enlace (modo demo)",
            demo_link_help: "Copia y pégalo en una nueva pestaña, o ábrelo abajo. En producción este enlace llegaría por email.",
            copy: "Copiar",
            link_copied: "Enlace copiado",
            open_reset_page: "Abrir página de restablecimiento",
            reset_password: "Nueva contraseña",
            new_password: "Nueva contraseña",
            confirm_password: "Confirmar contraseña",
            update_password: "Actualizar contraseña",
            password_updated: "Contraseña actualizada. Ya puedes iniciar sesión.",
            password_too_short: "La contraseña debe tener al menos 8 caracteres.",
            passwords_mismatch: "Las contraseñas no coinciden.",
            reset_missing_token: "Falta el token de recuperación. Solicita un enlace nuevo.",
        },
        settings: {
            eyebrow: "Información",
            title: "Ajustes",
            subtitle: "Sobre la app, legal y soporte.",
            language: {
                eyebrow: "Preferencias",
                title: "Idioma",
                subtitle: "Elige el idioma de la aplicación. Tu preferencia se guarda en tu cuenta.",
                spanish: "Español",
                english: "Inglés",
                anonymous_hint: "Como invitado, esta preferencia se guarda en este dispositivo. Al iniciar sesión usaremos el idioma de tu cuenta.",
            },
            about: {
                eyebrow: "Sobre la app",
                title: "Quiénes somos",
                p1: "Hola, somos Corazones A La Obra, un grupo de laicos enamorados de nuestro Señor Jesucristo, convencidos de que el mundo de hoy necesita testigos audaces de la Verdad. Como consagrados a la Santísima Virgen María, confiamos plenamente en su protección; ella es quien nos sostiene, nos abraza y nos conduce con mano firme hacia su Hijo. Bajo su manto, buscamos que cada una de nuestras acciones sea un puente hacia el Cielo.",
                p2: "Corazones A La Obra no es solo un proyecto digital; es nuestra respuesta al llamado a la santidad. Nuestra misión es poner nuestro tiempo, talento y tesoro al servicio de la Iglesia para la gloria de Dios y el bien de las almas.",
                p3_heading: "Acerca de soyapostol.org",
                p4: "Esta aplicación nace del deseo de santificar nuestra vida diaria y la de nuestros hermanos. Es un esfuerzo colectivo para que la Palabra de Dios, el Catecismo y la oración litúrgica estén siempre al alcance de tu mano, sin distracciones ni barreras y por supuesto completamente gratis. Todo ha sido desarrollado con el rigor y el fervor para que tú también puedas decir, con obras y de verdad: ¡Soy Apóstol!",
            },
            legal: {
                eyebrow: "Legal",
                title: "Legal",
                privacy_title: "Política de privacidad",
                privacy_hint: "Se abre en una nueva pestaña.",
                privacy_pending: "Próximamente disponible.",
                cookies_title: "Política de cookies",
                cookies_hint: "Cómo usamos cookies y tecnologías similares.",
                cookie_prefs_title: "Configuración de cookies",
                cookie_prefs_hint: "Revisa o cambia tus preferencias de consentimiento en cualquier momento.",
            },
            support: {
                eyebrow: "Soporte",
                title: "¿Necesitas ayuda?",
                cta: "Reportar un error o sugerir una mejora",
                subject: "Soporte soyapostol",
                body: "Hola, quisiera comentarles lo siguiente:\n\n",
            },
            footer: {
                version: "Versión {v}",
            },
            danger: {
                eyebrow: "Zona de peligro",
                title: "Eliminar mi cuenta",
                body: "Esta acción es permanente e irreversible. Antes de continuar, ten en cuenta que se borrarán definitivamente:",
                list_user: "Tu cuenta (correo, nombre, contraseña).",
                list_favs: "Todos tus favoritos guardados (lecturas, oraciones, Biblia, Catecismo, etc.).",
                list_sessions: "Tu sesión activa, los registros de inicio de sesión y los tokens de recuperación pendientes.",
                cta: "Eliminar mi cuenta",
                email_mismatch: "El correo no coincide con tu cuenta.",
                error: "No pudimos eliminar tu cuenta. Vuelve a intentarlo en unos instantes.",
                modal: {
                    eyebrow: "Confirmación final",
                    title: "¿Estás seguro?",
                    body: "Para confirmar el borrado permanente, escribe el correo de tu cuenta. Recibirás un email de despedida y serás redirigido al inicio.",
                    email_label: "Escribe tu correo: {email}",
                    cancel: "Cancelar",
                    confirm: "Sí, eliminar mi cuenta",
                    deleting: "Eliminando…",
                },
            },
            credits: {
                title: "Créditos",
                intro: "Agradecemos la generosidad de las plataformas que, a través de sus servicios, permiten que la Palabra y la vida de la Iglesia lleguen a cada rincón digital. Soy Apóstol integra contenidos de las siguientes fuentes a través de sus canales RSS autorizados:",
                row_readings_label: "Lecturas:",
                row_readings_value: "evangelizo.org y evangeli.net.",
                row_liturgy_label: "Liturgia de las Horas:",
                row_liturgy_value: "divineoffice.org e ibreviary.com.",
                row_news_label: "Noticias:",
                row_news_value: "vaticannews.va, aciprensa.com y ewtnnews.com.",
                lev: "Los textos del Catecismo de la Iglesia Católica, el Compendio y la Sagrada Biblia (versión oficial) son propiedad intelectual de la Santa Sede – Libreria Editrice Vaticana (© LEV). Su reproducción en esta plataforma se realiza con fines estrictamente pastorales y de formación laical, sin fines de lucro.",
                disclaimer: "Esta aplicación funciona como un agregador de fe; el contenido pertenece y es responsabilidad de sus respectivas fuentes originales.",
                right_to_forget_label: "Derecho al olvido:",
                right_to_forget_body: "puedes eliminar tu cuenta y todos los datos asociados en cualquier momento desde",
                right_to_forget_cta: "Ajustes → Zona de peligro → Eliminar mi cuenta",
            },
            fair_use: {
                title: "Uso Justo",
                p1: "Esta aplicación es una iniciativa sin fines de lucro, creada exclusivamente con fines de formación religiosa, estudio personal y evangelización. El uso de textos del Magisterio de la Iglesia Católica y la integración de canales RSS se realiza bajo la doctrina del «Fair Use» (Uso Justo), con el objetivo de difundir la Verdad y facilitar el acceso a los tesoros de la Fe.",
                p2: "No se percibe ningún beneficio económico por la distribución de estos contenidos. Todos los derechos de propiedad intelectual pertenecen a sus respectivos autores y editoriales (Libreria Editrice Vaticana, otras plataformas y agencias de noticias citadas). Si usted es el titular de los derechos de algún contenido y desea su retiro, por favor contáctenos inmediatamente.",
            },
        },
        account_deleted: {
            eyebrow: "Cuenta eliminada",
            title: "Gracias por haber estado aquí",
            body: "Tu cuenta y todos los datos asociados han sido eliminados de forma permanente. Te enviamos un correo de despedida con la confirmación.",
            farewell: "Que María, Madre de la Iglesia, te acompañe siempre.",
            cta: "Iniciar sesión",
        },
        nav_more: {
            settings: "Ajustes",
        },
    },
    en: {
        app_name: "soyapostol",
        tagline: "",
        nav: {
            dashboard: "Home",
            readings: "Readings",
            liturgy: "Liturgy of the Hours",
            prayers: "Prayers",
            rosary: "Holy Rosary",
            examen: "Examen of Conscience",
            news: "News",
            bible: "Bible",
            catechism: "Catechism",
            favorites: "Favorites",
            login: "Sign in",
            register: "Create account",
            logout: "Sign out",
            more: "More",
        },
        common: {
            loading: "Loading…",
            error: "Error",
            today: "Today",
            source: "Source",
            save_favorite: "Save to favorites",
            saved: "Saved",
            removed: "Removed",
            remove: "Remove",
            email: "Email",
            password: "Password",
            name: "Name",
            sign_in: "Sign in",
            create_account: "Create account",
            no_account: "Don't have an account?",
            have_account: "Already have an account?",
            welcome_back: "Welcome back",
            join_apostol: "Join Apostol",
            select: "Select",
            view: "View",
            search: "Search",
            refresh: "Refresh",
            back_to_menu: "Back to menu",
            back_to_top: "Back to top",
            yes: "Yes",
            cancel: "Cancel",
            show_more: "Read more",
            show_less: "Show less",
        },
        favorites: {
            search_placeholder: "Search my favorites…",
            no_results: 'No results for "{q}".',
        },
        sections: {
            readings_desc: "Today's readings with Gospel commentary",
            liturgy_desc: "Liturgy of the Hours prayer",
            prayers_desc: "Compendium of Catholic prayers",
            rosary_desc: "Pray the Holy Rosary, day by day",
            examen_desc: "Resources for your examination of conscience",
            news_desc: "The latest news from Vatican News",
            bible_desc: "The complete Catholic Holy Bible",
            catechism_desc: "Catechism of the Catholic Church",
            favorites_desc: "Your saved passages",
        },
        readings: {
            first: "1st Reading",
            psalm: "Psalm",
            second: "2nd Reading",
            gospel: "Gospel",
            gospel_acclamation: "Gospel Acclamation",
            commentary: "Commentary",
            universalis_unavailable: "Content temporarily unavailable",
            reflection_eyebrow: "Gospel commentary",
            reflection_title: "Daily reflection",
            reflection_credit: "Commentary courtesy of",
            eod_eyebrow: "Another reflection",
            eod_title: "Evangelio del Día commentary",
            eod_unavailable: "Evangelio del Día commentary is unavailable right now.",
            view_usccb: "Official readings (USCCB)",
            prev_day: "Previous day",
            next_day: "Next day",
            pick_date: "Pick a date",
            go_today: "Today",
        },
        usccb: {
            title: "Official Readings (USCCB)",
            close: "Back",
            refresh: "Refresh",
            loading: "Loading official readings…",
            open_external: "Open on USCCB.org",
            blocked_eyebrow: "Embedded view restricted",
            blocked_title: "USCCB does not allow its site to be shown inside the app",
            blocked_subtitle: "Because of the site's own security policies (X-Frame-Options), the content cannot be embedded. Open it in a new tab to read it.",
            try_again: "Try again",
        },
        news: {
            source_credit: "Source:",
            empty: "No news at the moment.",
            tab_all: "All",
            tabs_label: "News sources",
        },
        pwa: {
            eyebrow: "Available as an app",
            title: "Install soyapostol on your device",
            subtitle_native: "Launch straight from your home screen — no browser bar and full offline support.",
            subtitle_ios: "On iPhone or iPad you can add this app to your home screen in two quick steps.",
            ios_step_1: "Tap the Share button",
            ios_step_2: "Choose 'Add to Home Screen'",
            install_button: "Install the app",
            install_button_short: "Install",
            dismiss: "Dismiss",
        },
        rosary: {
            title: "Holy Rosary",
            today_eyebrow: "Today's Mysteries",
            choose_eyebrow: "Selected Mysteries",
            cover_subtitle: "Take your rosary, breathe deeply, and begin with a heart open to Mary.",
            begin: "Begin",
            change_mystery: "Change mystery",
            previous: "Previous",
            next: "Next",
            restart: "Restart",
            help: "Swipe, tap the sides, or use the keyboard arrows to navigate.",
            mystery_n: "Mystery {n}",
            fruit_label: "Fruit:",
            meditation_label: "Meditation:",
            decade_n: "Decade {n}",
            intro_three_eyebrow: "Three Hail Marys",
            offering_eyebrow: "Intentions",
            after_decade_label: "After the decade",
            after_decade_title: "Closing prayers",
            prayer_label: "Prayer",
            bead_progress: "{n} of {total}",
            tap_bead: "Tap to count",
            tap_anywhere: "Tap anywhere on the card to count",
            decade_done: "Decade complete",
            decade_done_hint: "Decade complete · press Next",
            reset: "Reset counter",
            completed_eyebrow: "Well done",
            completed_title: "You have completed the Holy Rosary",
            completed_subtitle: "May the Virgin Mary walk with you today and always.",
            pray_again: "Pray again",
        },
        examen: {
            title: "Examen of Conscience",
            cover_title: "Examen of Conscience",
            cover_subtitle: "Prepare for the sacrament of Reconciliation.",
            privacy_notice: "Privacy: all your answers are saved on this device only.",
            choose_profile: "Choose your examen",
            change_profile: "Change examen",
            commandment: "Commandment",
            your_state: "Your state",
            selected_count: "Marked in this examen",
            marked_count: "{n} marked",
            marked_count_one: "{n} marked",
            cumulative_title: "Your cumulative examen",
            see_summary: "See summary",
            summary_eyebrow: "Cumulative examen",
            summary_title: "Summary for Confession",
            summary_subtitle: "{n} thought(s), word(s) or deed(s) to bring to confession.",
            summary_empty: "You haven't marked any question yet.",
            back_to_questions: "Back to the examen",
            contrition_eyebrow: "To close",
            contrition_title: "Act of Contrition",
            start_over: "Start over",
            start_over_confirm: "Erase every mark and start over?",
            finish_confession: "Erase and exit",
            finish_confession_confirm: "This will permanently erase everything. End confession?",
            finish_done_title: "Peace be with you",
            finish_done_subtitle: "All your marks have been erased. Go in peace.",
            return_home: "Return home",
            scripture_eyebrow: "Word of God",
            prayer_eyebrow: "Closing prayer",
            profile: {
                adults: "Adults",
                married_couples: "Married",
                single_adults: "Single",
                teenagers: "Teenagers",
                beatitudes: "Beatitudes",
                triple_love: "Threefold love",
                capital_sins: "Seven capital sins",
                virtues: "Virtues",
                mercy_works: "Works of mercy",
                ignatian_daily: "Daily examen (Ignatian)",
                charity_hymn: "Hymn of Love",
                holy_spirit: "Gifts of the Holy Spirit",
                youth: "Young people",
                work: "Professional work",
                family: "Marriage and family",
                digital: "Digital life",
            },
            profile_desc: {
                adults: "General examen on the Ten Commandments.",
                married_couples: "Adds specific questions for spouses.",
                single_adults: "Adds specific questions for single adults.",
                teenagers: "Adds specific questions for teenagers.",
                beatitudes: "Alternative examen based on the eight Beatitudes.",
                triple_love: "Based on the Great Commandment: love of God, neighbor, and self.",
                capital_sins: "An analysis of the roots of our faults: the seven capital sins.",
                virtues: "The theological and cardinal virtues that guide Christian life.",
                mercy_works: "The corporal and spiritual works of mercy.",
                ignatian_daily: "A daily review of the heart in five steps (St. Ignatius of Loyola).",
                charity_hymn: "Based on 1 Corinthians 13: the qualities of true love.",
                holy_spirit: "How you are living the gifts of the Holy Spirit received at Confirmation.",
                youth: "A practical guide for young people: home, school, and friends.",
                work: "For professionals: integrity, excellence, and Christian leadership.",
                family: "For spouses and parents: living the vocation of the Christian home.",
                digital: "Our use of social media, the internet, and screens in the light of the faith.",
            },
        },
        catechism: {
            search_placeholder: "Search by number (e.g. 144) or text…",
            results_count: "{count} result(s)",
            total_count: "{count} paragraphs",
            no_results: "No results. Try another term or number.",
            load_more: "Load more",
            copy_paragraph: "Copy paragraph",
            share_paragraph: "Share",
            remove_favorite: "Remove from favorites",
            paragraph_copied: "Paragraph copied",
            share_title: "Catechism of the Catholic Church",
            back_to_index: "Index",
            coming_soon_eyebrow: "Coming soon",
            coming_soon_title: "Catechism in Spanish",
            coming_soon_body: "We are preparing the official Spanish edition of the Catechism of the Catholic Church. In the meantime, you can read it in English using the language toggle in Settings.",
        },
        bible: {
            search_placeholder: "Search keyword or reference (e.g. \"John 3:16\"). Press Enter to jump.",
            results_count: "{count} result(s)",
            no_results: "No results. Try another word or reference.",
            go_to: "Go to",
            copy_verse: "Copy verse",
            share_verse: "Share",
            remove_favorite: "Remove from favorites",
            verse_copied: "Verse copied",
            share_title: "Bible verse",
            saved_hint: "Saved to favorites",
            back_to_index: "Index",
        },
        prayers_actions: {
            copy: "Copy prayer",
            share: "Share",
            copied: "Prayer copied",
            share_title: "Prayer",
            long_press_hint: "Long-press for more options",
        },
        news_actions: {
            copy: "Copy article",
            share: "Share article",
            copied: "Article copied",
        },
        votd: {
            title: "Verse of the day",
            open_bible: "Open in Bible",
        },
        quickview: {
            open_bible: "Open in Bible",
            not_found: "Could not load reference {ref}.",
        },
        admin: {
            eyebrow: "Admin panel",
            new_prayer: "New prayer",
            edit_prayer: "Edit prayer",
            db_hint: "Prayers are stored in the database. Your changes persist for everyone.",
            title: "Title",
            category: "Category",
            choose_category: "Choose a category…",
            new_category: "+ New category",
            new_category_name: "New category name",
            content: "Prayer text",
            content_placeholder: "Paste the text here. A blank line separates paragraphs.",
            add: "Add prayer",
            save: "Save changes",
            cancel: "Cancel",
            edit: "Edit",
            delete: "Delete",
            stored_count: "{count} prayers",
            empty: "No prayers yet.",
            no_filter_matches: "No matches for that filter.",
            filter_placeholder: "Filter by title or category…",
            imported_badge: "Imported",
            created: "Prayer added",
            updated: "Prayer updated",
            deleted: "Prayer deleted",
            confirm_delete: "Delete \"{title}\"?",
            missing_fields: "Complete title, category and text.",
        },
        auth: {
            forgot_password: "Forgot your password?",
            recover_eyebrow: "Recover access",
            forgot_password_intro: "We'll send you a link to reset your password. It expires in 60 minutes.",
            send_reset_link: "Send reset link",
            reset_link_issued: "Reset link issued",
            reset_link_demo_hint: "Demo mode: copy the link shown below to continue.",
            reset_link_sent_generic: "If the email exists, we sent you a reset link.",
            check_inbox_title: "Check your inbox",
            check_inbox_hint: "If {email} matches an account, we've just sent you an email with a link to reset your password.",
            check_spam_hint: "The link expires in 60 minutes. If you don't see it, check your spam or promotions folder.",
            demo_link_label: "Your link (demo mode)",
            demo_link_help: "Copy and paste it into a new tab, or open it below. In production this link would arrive by email.",
            copy: "Copy",
            link_copied: "Link copied",
            open_reset_page: "Open reset page",
            reset_password: "New password",
            new_password: "New password",
            confirm_password: "Confirm password",
            update_password: "Update password",
            password_updated: "Password updated. You can sign in now.",
            password_too_short: "Password must be at least 8 characters.",
            passwords_mismatch: "Passwords do not match.",
            reset_missing_token: "Reset token missing. Request a new link.",
        },
        settings: {
            eyebrow: "Information",
            title: "Settings",
            subtitle: "About the app, legal, and support.",
            language: {
                eyebrow: "Preferences",
                title: "Language",
                subtitle: "Choose the app's language. Your preference is saved to your account.",
                spanish: "Spanish",
                english: "English",
                anonymous_hint: "As a guest, this preference is saved on this device. When you sign in we'll use your account's language.",
            },
            about: {
                eyebrow: "About the app",
                title: "Who we are",
                p1: "Hello, we are Corazones A La Obra, a group of lay people in love with our Lord Jesus Christ, convinced that today's world needs bold witnesses of the Truth. As those consecrated to the Blessed Virgin Mary, we place our full trust in her protection; she is the one who sustains us, embraces us, and leads us with a firm hand to her Son. Under her mantle, we seek to make each of our actions a bridge to Heaven.",
                p2: "Corazones A La Obra is not just a digital project; it is our response to the call to holiness. Our mission is to place our time, talent, and treasure at the service of the Church for the glory of God and the good of souls.",
                p3_heading: "About soyapostol.org",
                p4: "This app is born from the desire to sanctify our daily life and that of our brothers and sisters. It is a collective effort so that the Word of God, the Catechism, and liturgical prayer are always within your reach, without distractions or barriers and of course completely free. Everything has been developed with the rigor and fervor so that you too can say, in deed and in truth: I Am an Apostle!",
            },
            legal: {
                eyebrow: "Legal",
                title: "Legal",
                privacy_title: "Privacy Policy",
                privacy_hint: "Opens in a new tab.",
                privacy_pending: "Coming soon.",
                cookies_title: "Cookie Policy",
                cookies_hint: "How we use cookies and similar technologies.",
                cookie_prefs_title: "Cookie preferences",
                cookie_prefs_hint: "Review or change your consent at any time.",
            },
            support: {
                eyebrow: "Support",
                title: "Need help?",
                cta: "Report a bug or suggest an improvement",
                subject: "soyapostol support",
                body: "Hi, I wanted to share the following:\n\n",
            },
            footer: {
                version: "Version {v}",
            },
            danger: {
                eyebrow: "Danger zone",
                title: "Delete my account",
                body: "This action is permanent and irreversible. Before continuing, note that the following will be deleted:",
                list_user: "Your account (email, name, password).",
                list_favs: "All your saved favorites (readings, prayers, Bible, Catechism, etc.).",
                list_sessions: "Your active session, login records, and any pending recovery tokens.",
                cta: "Delete my account",
                email_mismatch: "The email does not match your account.",
                error: "We couldn't delete your account. Please try again in a moment.",
                modal: {
                    eyebrow: "Final confirmation",
                    title: "Are you sure?",
                    body: "To confirm permanent deletion, type the email of your account. You will receive a farewell email and be redirected to the home page.",
                    email_label: "Type your email: {email}",
                    cancel: "Cancel",
                    confirm: "Yes, delete my account",
                    deleting: "Deleting…",
                },
            },
            credits: {
                title: "Credits",
                intro: "We are grateful to the platforms whose services allow the Word and the life of the Church to reach every digital corner. Soy Apóstol integrates content from the following sources through their authorised RSS channels:",
                row_readings_label: "Readings:",
                row_readings_value: "evangelizo.org and evangeli.net.",
                row_liturgy_label: "Liturgy of the Hours:",
                row_liturgy_value: "divineoffice.org and ibreviary.com.",
                row_news_label: "News:",
                row_news_value: "vaticannews.va, aciprensa.com and ewtnnews.com.",
                lev: "The texts of the Catechism of the Catholic Church, the Compendium, and the Holy Bible (official edition) are the intellectual property of the Holy See – Libreria Editrice Vaticana (© LEV). Their reproduction on this platform is carried out for strictly pastoral and lay-formation purposes, on a non-profit basis.",
                disclaimer: "This application operates as a faith aggregator; the content belongs to and is the responsibility of its respective original sources.",
                right_to_forget_label: "Right to be forgotten:",
                right_to_forget_body: "you can delete your account and all associated data at any time from",
                right_to_forget_cta: "Settings → Danger zone → Delete my account",
            },
            fair_use: {
                title: "Fair Use",
                p1: "This application is a non-profit initiative, created exclusively for religious formation, personal study, and evangelisation. The use of texts from the Magisterium of the Catholic Church and the integration of RSS channels is carried out under the doctrine of «Fair Use», with the aim of spreading the Truth and facilitating access to the treasures of the Faith.",
                p2: "No economic benefit is received from the distribution of this content. All intellectual property rights belong to their respective authors and publishers (Libreria Editrice Vaticana, other platforms, and the news agencies cited). If you are the rights holder of any content and wish to have it removed, please contact us immediately.",
            },
        },
        account_deleted: {
            eyebrow: "Account deleted",
            title: "Thank you for being here",
            body: "Your account and all associated data have been permanently deleted. We sent you a farewell email with the confirmation.",
            farewell: "May Mary, Mother of the Church, always watch over you.",
            cta: "Sign in",
        },
        nav_more: {
            settings: "Settings",
        },
    },
};

const LangContext = createContext(null);

const SUPPORTED = ["es", "en"];
const DEFAULT_LANG = "es";

function readInitialLang() {
    // Anonymous default is Spanish. localStorage holds the anonymous
    // preference so a user who toggles lang while logged-out keeps it
    // across reloads. Logged-in users override this from the server.
    try {
        const stored = localStorage.getItem("apostol_lang");
        return SUPPORTED.includes(stored) ? stored : DEFAULT_LANG;
    } catch {
        return DEFAULT_LANG;
    }
}

export function LangProvider({ children }) {
    const { user, setUser } = useAuth();
    const [lang, setLangState] = useState(readInitialLang);

    // Persist anonymous preference (and last-known UI lang) locally.
    useEffect(() => {
        try { localStorage.setItem("apostol_lang", lang); } catch { /* ignore */ }
    }, [lang]);

    // When the user object resolves with a stored `lang`, adopt it. We only
    // honour the server pref the first time a user appears in this session
    // so that a logged-in toggle isn't immediately overwritten by a stale
    // /me payload.
    const adoptedForUserId = useRef(null);
    useEffect(() => {
        if (!user || !user.id) return;
        if (adoptedForUserId.current === user.id) return;
        adoptedForUserId.current = user.id;
        if (SUPPORTED.includes(user.lang) && user.lang !== lang) {
            setLangState(user.lang);
        }
    }, [user, lang]);

    // Reset the "adopted" guard on logout so a future login is honoured again.
    useEffect(() => {
        if (user === false) adoptedForUserId.current = null;
    }, [user]);

    const setLang = (next) => {
        if (!SUPPORTED.includes(next)) return;
        setLangState(next);
        // Persist to the user record (best-effort) when logged in.
        if (user && user.id) {
            api.patch("/auth/me", { lang: next })
                .then((res) => {
                    if (res.data && setUser) setUser(res.data);
                })
                .catch(() => { /* anonymous fallback already saved locally */ });
        }
    };

    const t = (path, vars) => {
        const parts = path.split(".");
        let v = STRINGS[lang];
        for (const p of parts) {
            if (v && Object.prototype.hasOwnProperty.call(v, p)) v = v[p];
            else return path;
        }
        if (typeof v === "string" && vars) {
            return v.replace(/\{(\w+)\}/g, (m, k) =>
                Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : m);
        }
        return v;
    };
    return (
        <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>
    );
}

export const useLang = () => useContext(LangContext);
